import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, ChevronLeft, GraduationCap, ArrowUp, ArrowDown } from "lucide-react";

const ROLES = ["HOUSEKEEPER", "SECURITY", "RECEPTIONIST", "OTHER", "ALL"];
const EMPTY_SLIDE = { text: "", text_hi: "", text_bn: "", image_photo_id: null, youtube_url: "" };
const EMPTY_Q = { q_text: "", options: ["", "", "", ""], correct_index: 0, q_text_hi: "", q_text_bn: "", options_hi: null, options_bn: null };

export default function CourseBuilderPage() {
    const { courseId } = useParams();
    const nav = useNavigate();
    const isNew = !courseId || courseId === "new";
    const [form, setForm] = useState({
        title: "", description: "", target_role: "ALL",
        pass_mark: 4, published: true,
        slides: [{ ...EMPTY_SLIDE }],
        quiz: Array.from({ length: 5 }, () => ({ ...EMPTY_Q, options: ["", "", "", ""] })),
    });
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isNew) return;
        setLoading(true);
        api.get(`/courses/${courseId}`).then(({ data }) => {
            setForm({
                title: data.title || "",
                description: data.description || "",
                target_role: data.target_role || "ALL",
                pass_mark: data.pass_mark || 4,
                published: data.published !== false,
                slides: (data.slides?.length ? data.slides : [{ ...EMPTY_SLIDE }]).map((s) => ({ ...EMPTY_SLIDE, ...s })),
                quiz: (data.quiz?.length ? data.quiz : Array.from({ length: 5 }, () => ({ ...EMPTY_Q, options: ["", "", "", ""] }))).map((q) => ({ ...EMPTY_Q, ...q, options: q.options || ["", "", "", ""] })),
            });
        }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
    }, [courseId, isNew]);

    function updSlide(i, patch) { setForm((f) => ({ ...f, slides: f.slides.map((s, j) => j === i ? { ...s, ...patch } : s) })); }
    function updQ(i, patch) { setForm((f) => ({ ...f, quiz: f.quiz.map((q, j) => j === i ? { ...q, ...patch } : q) })); }
    function updOpt(i, k, v) { setForm((f) => ({ ...f, quiz: f.quiz.map((q, j) => j === i ? { ...q, options: q.options.map((o, m) => m === k ? v : o) } : q) })); }
    function moveSlide(i, dir) { const j = i + dir; if (j < 0 || j >= form.slides.length) return; const arr = [...form.slides]; [arr[i], arr[j]] = [arr[j], arr[i]]; setForm({ ...form, slides: arr }); }

    async function save() {
        setSaving(true);
        try {
            const payload = {
                ...form,
                slides: form.slides.map((s) => ({ ...s, text_hi: s.text_hi || null, text_bn: s.text_bn || null, youtube_url: s.youtube_url || null, image_photo_id: s.image_photo_id || null })),
                quiz: form.quiz.map((q) => ({ ...q, q_text_hi: q.q_text_hi || null, q_text_bn: q.q_text_bn || null, options_hi: q.options_hi || null, options_bn: q.options_bn || null })),
            };
            const { data } = isNew ? await api.post("/courses", payload) : await api.patch(`/courses/${courseId}`, payload);
            toast.success(isNew ? "Course created" : "Course updated");
            nav(`/admin/courses/${data.id}`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    }

    async function del() {
        if (isNew) return;
        if (!window.confirm(`Delete "${form.title}"?`)) return;
        try { await api.delete(`/courses/${courseId}`); toast.success("Deleted"); nav("/training"); }
        catch (e) { toast.error(apiError(e)); }
    }

    if (loading) return <div className="mx-auto max-w-4xl p-6"><div className="h-64 bg-white rounded-xl animate-pulse" /></div>;

    return (
        <div className="mx-auto max-w-4xl px-4 sm:px-6 py-6 sm:py-10">
            <Link to="/training" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#1E3355] mb-3"><ChevronLeft className="w-4 h-4" /> All courses</Link>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">HO Admin</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1 flex items-center gap-2"><GraduationCap className="w-6 h-6" /> {isNew ? "New course" : "Edit course"}</h1>
                </div>
                <div className="flex items-center gap-2">
                    {!isNew && <button onClick={del} className="inline-flex items-center gap-1 text-xs font-semibold text-[#E02B2B] bg-white border border-red-200 hover:bg-red-50 px-3 py-2 rounded-md" data-testid="course-delete-btn"><Trash2 className="w-3.5 h-3.5" /> Delete</button>}
                    <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-60" data-testid="course-save-btn">
                        <Save className="w-4 h-4" /> {saving ? "Saving…" : (isNew ? "Create course" : "Save changes")}
                    </button>
                </div>
            </div>

            <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card p-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Title"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className={fieldCls} data-testid="course-title" /></Field>
                    <Field label="Target role">
                        <select value={form.target_role} onChange={(e) => setForm({ ...form, target_role: e.target.value })} className={fieldCls} data-testid="course-target-role">
                            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </Field>
                </div>
                <Field label="Description"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className={fieldCls} data-testid="course-desc" /></Field>
                <div className="grid grid-cols-2 gap-4">
                    <Field label="Pass mark (out of 5)">
                        <input type="number" min={1} max={5} value={form.pass_mark} onChange={(e) => setForm({ ...form, pass_mark: parseInt(e.target.value) || 4 })} className={fieldCls} data-testid="course-passmark" />
                    </Field>
                    <label className="flex items-center gap-2 text-sm mt-6">
                        <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="course-published" /> Published (visible to staff)
                    </label>
                </div>
            </div>

            {/* Slides */}
            <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-[#1E3355]">Slides ({form.slides.length})</div>
                    <button onClick={() => setForm({ ...form, slides: [...form.slides, { ...EMPTY_SLIDE }] })} className="text-xs font-medium text-[#1E3355] hover:underline" data-testid="add-slide">+ Add slide</button>
                </div>
                <div className="space-y-3">
                    {form.slides.map((s, i) => (
                        <div key={i} className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid={`slide-${i}`}>
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Slide {i + 1}</div>
                                <div className="flex items-center gap-1">
                                    <button onClick={() => moveSlide(i, -1)} className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-30" disabled={i === 0}><ArrowUp className="w-3.5 h-3.5" /></button>
                                    <button onClick={() => moveSlide(i, 1)} className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-30" disabled={i === form.slides.length - 1}><ArrowDown className="w-3.5 h-3.5" /></button>
                                    <button onClick={() => setForm({ ...form, slides: form.slides.filter((_, j) => j !== i) })} className="p-1 rounded hover:bg-red-50 text-[#E02B2B]" data-testid={`remove-slide-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                            </div>
                            <textarea placeholder="Slide text (English)" value={s.text} onChange={(e) => updSlide(i, { text: e.target.value })} rows={2} className={fieldCls} data-testid={`slide-text-${i}`} />
                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <input placeholder="हिन्दी (optional)" value={s.text_hi || ""} onChange={(e) => updSlide(i, { text_hi: e.target.value })} className={`${fieldCls} text-xs`} data-testid={`slide-text-hi-${i}`} />
                                <input placeholder="বাংলা (optional)" value={s.text_bn || ""} onChange={(e) => updSlide(i, { text_bn: e.target.value })} className={`${fieldCls} text-xs`} data-testid={`slide-text-bn-${i}`} />
                            </div>
                            <div className="mt-2">
                                <input placeholder="YouTube URL (optional, embedded video)" value={s.youtube_url || ""} onChange={(e) => updSlide(i, { youtube_url: e.target.value })} className={`${fieldCls} text-xs`} data-testid={`slide-yt-${i}`} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Quiz */}
            <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-semibold text-[#1E3355]">Quiz ({form.quiz.length} questions — recommend 5)</div>
                    <button onClick={() => setForm({ ...form, quiz: [...form.quiz, { ...EMPTY_Q, options: ["", "", "", ""] }] })} className="text-xs font-medium text-[#1E3355] hover:underline" data-testid="add-question">+ Add question</button>
                </div>
                <div className="space-y-3">
                    {form.quiz.map((q, i) => (
                        <div key={i} className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid={`question-${i}`}>
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Question {i + 1}</div>
                                <button onClick={() => setForm({ ...form, quiz: form.quiz.filter((_, j) => j !== i) })} className="p-1 rounded hover:bg-red-50 text-[#E02B2B]" data-testid={`remove-question-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                            <input placeholder="Question text (English)" value={q.q_text} onChange={(e) => updQ(i, { q_text: e.target.value })} className={fieldCls} data-testid={`q-text-${i}`} />
                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <input placeholder="हिन्दी (optional)" value={q.q_text_hi || ""} onChange={(e) => updQ(i, { q_text_hi: e.target.value })} className={`${fieldCls} text-xs`} data-testid={`q-text-hi-${i}`} />
                                <input placeholder="বাংলা (optional)" value={q.q_text_bn || ""} onChange={(e) => updQ(i, { q_text_bn: e.target.value })} className={`${fieldCls} text-xs`} data-testid={`q-text-bn-${i}`} />
                            </div>
                            <div className="mt-2 space-y-1.5">
                                {q.options.map((opt, k) => (
                                    <div key={k} className="flex items-center gap-2">
                                        <label className="inline-flex items-center gap-1 cursor-pointer">
                                            <input type="radio" name={`correct-${i}`} checked={q.correct_index === k} onChange={() => updQ(i, { correct_index: k })} data-testid={`q-${i}-correct-${k}`} />
                                        </label>
                                        <input placeholder={`Option ${k + 1}`} value={opt} onChange={(e) => updOpt(i, k, e.target.value)} className={`flex-1 ${fieldCls} text-sm ${q.correct_index === k ? "border-emerald-400 bg-emerald-50" : ""}`} data-testid={`q-${i}-opt-${k}`} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

const fieldCls = "w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355] bg-white";
function Field({ label, children }) { return (<div><label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>{children}</div>); }
